import { useMemo } from "react";
import { ThreadId, type ScopedThreadRef } from "@cmmd-forge/contracts";
import { scopeThreadRef } from "@cmmd-forge/client-runtime";
import { useShallow } from "zustand/shallow";

import {
  type CloudProjectCatalogRow,
  useCmmdForgeRuntimeListSnapshot,
} from "../cmmd/cmmdForgeRuntimeListResource";
import {
  type CloudRuntimeProjectHandoff,
  useCloudRuntimeProjectHandoffStore,
} from "../components/sidebar/cloudRuntimeProjectHandoffStore";
import { useComposerDraftStore } from "../composerDraftStore";
import { classifyEnvironmentId } from "../environments/environmentIdClassification";
import {
  createThreadSelectorAcrossEnvironments,
  type DurableScopeResolver,
} from "../storeSelectors";
import { useStore } from "../store";
import { resolveThreadRouteRef } from "../threadRoutes";
import { findCloudRowForEnvironment } from "./cloudRouteCatalogRow";
import { resolveCanonicalThreadRouteRef } from "../threadRouteResolution";

/**
 * Builds the resolver that names the durable Environment a legacy runtime id belongs to. The
 * catalog and the handoffs are passed in so the caller can subscribe to them: a mapping that
 * appears after both scopes exist has to re-run the thread lookup, not wait for the next
 * unrelated store update. Null for any other id shape, and while the catalog does not know
 * the runtime.
 */
export function makeDurableScopeResolver(
  rows: readonly CloudProjectCatalogRow[],
  handoffs: readonly CloudRuntimeProjectHandoff[],
): DurableScopeResolver {
  return (environmentId) => {
    if (classifyEnvironmentId(environmentId).shape !== "legacy-runtime") return null;
    return findCloudRowForEnvironment(environmentId, rows, handoffs)?.row.cmmdEnvironmentId ?? null;
  };
}

/**
 * Which thread the route is actually looking at, as a stable reference.
 *
 * The `-` prefix keeps TanStack's file-based routing from treating this as a
 * route: in that directory a dot is a path separator and a non-route file is a
 * build warning.
 */
export function useCanonicalRouteThreadRef(
  environmentIdParam: string,
  threadIdParam: string,
): ScopedThreadRef | null {
  const routeThreadRef = useMemo(
    () => resolveThreadRouteRef({ environmentId: environmentIdParam, threadId: threadIdParam }),
    [environmentIdParam, threadIdParam],
  );
  const draftBackedThreadRef = useComposerDraftStore(
    useShallow((store) => {
      for (const draftThread of Object.values(store.draftThreadsByThreadKey)) {
        if (draftThread.threadId !== threadIdParam) continue;
        return (
          draftThread.promotedTo ?? scopeThreadRef(draftThread.environmentId, draftThread.threadId)
        );
      }
      return null;
    }),
  );
  const catalogRows = useCmmdForgeRuntimeListSnapshot().rows;
  const handoffsByRuntimeId = useCloudRuntimeProjectHandoffStore((state) => state.byRuntimeId);
  const threadAcrossEnvironments = useStore(
    useMemo(
      () =>
        createThreadSelectorAcrossEnvironments(
          ThreadId.make(threadIdParam),
          makeDurableScopeResolver(catalogRows, Object.values(handoffsByRuntimeId)),
        ),
      [catalogRows, handoffsByRuntimeId, threadIdParam],
    ),
  );
  const canonical = useMemo(
    () =>
      routeThreadRef
        ? resolveCanonicalThreadRouteRef({
            routeThreadRef,
            draftBackedThreadRef,
            threadAcrossEnvironments,
          })
        : null,
    [draftBackedThreadRef, routeThreadRef, threadAcrossEnvironments],
  );

  // Re-keyed on the resolved ids so the ref keeps one identity while they hold.
  // `threadAcrossEnvironments` changes identity on every store update, so
  // without this the ref did too, and everything keyed on it churned once per
  // update: five selector caches reset, and the thread detail subscription was
  // torn down and re-established.
  const canonicalEnvironmentId = canonical?.environmentId ?? null;
  const canonicalThreadId = canonical?.threadId ?? null;
  return useMemo(
    () =>
      canonicalEnvironmentId !== null && canonicalThreadId !== null
        ? scopeThreadRef(canonicalEnvironmentId, canonicalThreadId)
        : null,
    [canonicalEnvironmentId, canonicalThreadId],
  );
}
